import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { Entity } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { maxLength, minLength } from '@adornis/validation/functions/length.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { digitsOnly } from '@martinus/validation/digitsOnly.js';
import { startsWith } from '@martinus/validation/startsWith.js';
import { m } from '../../localization/out/messages.js';

export enum BillingRhythm {
  ONCE_CUSTOM = 'once_custom',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export const BillingPositionPriceSymbols = {
  SELF_SELECTION: 'null',
  DYNAMIC_PRICE: '-1',

  /**
   * All these are technically 0 in price but we need distinction so personal can see why a person is paying 0
   */
  PAUSED: '-2',
  EXCEMPT: '-3',
  FUNDED: '-4',
};

/**
 * Prices are held in cents, the symbols above in Euro - the dynamic symbol `-1` is stored as `-100`.
 */
export const DYNAMIC_PRICE_IN_CENTS = parseInt(BillingPositionPriceSymbols.DYNAMIC_PRICE, 10) * 100;

/**
 * A dynamic price is a placeholder, not a price: the real amount is filled in later, per usage.
 * It must not be confused with the other negative symbols (paused/exempt/funded), which all mean
 * "this person pays nothing".
 */
export const isDynamicPriceInCents = (priceInCents: Maybe<number>): boolean => priceInCents === DYNAMIC_PRICE_IN_CENTS;

export const translateBillingRhythm = (rhythm: BillingRhythm): string => {
  const translations: Record<BillingRhythm, string> = {
    [BillingRhythm.ONCE_CUSTOM]: m._enum_billingrhytm_once_custom(),
    [BillingRhythm.MONTHLY]: m._enum_billingrhytm_monthly(),
    [BillingRhythm.YEARLY]: m._enum_billingrhytm_yearly(),
  };
  return translations[rhythm];
};

@Entity()
export class BillingPosition extends AdornisEntity {
  static override _class = 'BillingPosition';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  constructor(doc: PartialEntityData<BillingPosition>) {
    super(doc);
  }

  // which template is this billing position based on
  @Field(() => String)
  public templateID: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public name: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public kuerzel: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public kostenstelleID: Maybe<string>;

  @Field(() => Int)
  public priceInCents: Maybe<number>;

  /**
   * When a booking of this position is due: monthly in every run, yearly once every twelve months,
   * once until it has been billed. There is no billing month - the group is exported monthly and a
   * run collects whoever is due (`isDueOn` in `sepa/api/mandateActivity.ts`).
   */
  @Field(() => String)
  public rhythm: Maybe<BillingRhythm>;

  @validate(and(nonOptional(), minLength(6), maxLength(6), startsWith('8'), digitsOnly()))
  @Field(() => String)
  public buchungskonto: Maybe<string>;
}
